import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion'; // Framer Motion animasyonları için
import "./ChatBot.css";

const Chatbot = () => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [typingEffect, setTypingEffect] = useState(false);
  const messagesEndRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false); // Büyültme/küçültme durumu
  const inputRef = useRef(null);
  const chatWindowRef = useRef(null);

  // Mesajların en alta kaydırılması için
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [messages, isOpen]);

  // Chatbot açılma/kapanma animasyonu için
  useEffect(() => {
    if (isOpen && chatWindowRef.current) {
      // Chatbot açıldığında intro animasyonu
      const chatWindow = chatWindowRef.current;
      chatWindow.classList.add('chat-intro');
      
      setTimeout(() => {
        chatWindow.classList.remove('chat-intro');
      }, 1000);
    }
  }, [isOpen]);

  // Chatbot açılma/kapanma animasyonu için
  useEffect(() => {
    if (isOpen && chatWindowRef.current) {
      // Chatbot açıldığında intro animasyonu
      const chatWindow = chatWindowRef.current;
      chatWindow.classList.add('chat-intro');
      
      setTimeout(() => {
        chatWindow.classList.remove('chat-intro');
      }, 1000);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { text: input, sender: "user", timestamp: new Date() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post("https://ajan.services/api/chat", {
        message: input,
        history: messages.map((m) => ({
          role: m.sender === "user" ? "user" : "assistant",
          content: m.text,
        })),
      });

      setTypingEffect(true);
      
      // Bir yazma efekti için timeout
      setTimeout(() => {
        setLoading(false);
        setTypingEffect(false);
        
        setMessages([
          ...newMessages, 
          { 
            text: response.data.message, 
            sender: "bot", 
            timestamp: new Date() 
          }
        ]);
      }, 1000);
    } catch (error) {
      console.error("Mesaj gönderme hatası:", error);
      setLoading(false);
      
      setMessages([
        ...newMessages, 
        { 
          text: t('chatbot.errorMessage'), 
          sender: "bot", 
          timestamp: new Date() 
        }
      ]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Büyültme/küçültme işlemini yönet
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Logo için pulsar animasyonu varyantları
  const logoVariants = {
    pulse: {
      scale: [1, 1.1, 1],
      opacity: [0.8, 1, 0.8],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "reverse"
      }
    }
  };

  // Chat penceresi animasyon varyantları
  const chatWindowVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: "spring", 
        duration: 0.5,
        stiffness: 100
      }
    },
    exit: { 
      opacity: 0, 
      y: 20, 
      scale: 0.9,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  return (
    <div className="chatbot-container">
      {!isOpen ? (
        <motion.button 
          onClick={() => setIsOpen(true)} 
          className="chat-button"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ scale: 0 }}
          animate={{ scale: 1, rotate: [0, 10, 0] }}
          transition={{ 
            type: "spring", 
            stiffness: 260, 
            damping: 20,
            rotate: { duration: 1, repeat: 0 }
          }}
        >
          <motion.div
            variants={logoVariants}
            animate="pulse"
          >
            {/* Ajan Logo - Kendi logonuzu img olarak ekleyin */}
            <img 
              src="/logo.svg" 
              alt="AJAN 007" 
              className="agent-logo" 
              onError={(e) => {
                // Logo yüklenemezse fallback olarak icon göster
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
            {/* Fallback icon - Logo yüklenmezse görünür */}
            <svg
              style={{ display: 'none' }}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </motion.div>
        </motion.button>
      ) : (
        <AnimatePresence>
          <motion.div 
            className={`chat-window ${isExpanded ? 'expanded' : ''}`}
            ref={chatWindowRef}
            variants={chatWindowVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="chat-header">
              <div className="header-content">
                <motion.img 
                  src="/logo.svg" 
                  alt="AJAN 007" 
                  className="header-logo"
                  initial={{ rotate: 0 }}
                  animate={{ rotate: [0, 5, 0, -5, 0] }}
                  transition={{ 
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 5
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <h3>{t('chatbot.title')}</h3>
              </div>
              
              <div className="header-actions">
                {/* Büyültme/Küçültme Butonu */}
                <motion.button 
                  onClick={toggleExpand} 
                  className="expand-button"
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  title={isExpanded ? t('chatbot.minimize') : t('chatbot.maximize')}
                >
                  {isExpanded ? (
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <polyline points="4 14 10 14 10 20"></polyline>
                      <polyline points="20 10 14 10 14 4"></polyline>
                      <line x1="14" y1="10" x2="21" y2="3"></line>
                      <line x1="3" y1="21" x2="10" y2="14"></line>
                    </svg>
                  ) : (
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <polyline points="9 21 3 21 3 15"></polyline>
                      <line x1="21" y1="3" x2="14" y2="10"></line>
                      <line x1="3" y1="21" x2="10" y2="14"></line>
                    </svg>
                  )}
                </motion.button>
                
                {/* Kapatma Butonu */}
                <motion.button 
                  onClick={() => setIsOpen(false)} 
                  className="close-button"
                  whileHover={{ scale: 1.2, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </motion.button>
              </div>
            </div>
            
            <div className="chat-messages">
              {messages.length === 0 && (
                <motion.div 
                  className="welcome-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    type: "spring", 
                    delay: 0.3,
                    stiffness: 100
                  }}
                >
                  <div className="bot-avatar">
                    <img 
                      src="/logo.svg" 
                      alt="AJAN 007" 
                      className="agent-avatar"
                      onError={(e) => {
                        // Logo yüklenemezse fallback
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'block';
                      }}
                    />
                    <svg
                      style={{ display: 'none' }}
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                      <line x1="9" y1="9" x2="9.01" y2="9"></line>
                      <line x1="15" y1="9" x2="15.01" y2="9"></line>
                    </svg>
                  </div>
                  <p>{t('chatbot.welcomeMessage')}</p>
                </motion.div>
              )}
              
              {messages.map((msg, index) => (
                <motion.div 
                  key={index} 
                  className={`message ${msg.sender}`}
                  initial={{ opacity: 0, x: msg.sender === "user" ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ 
                    type: "spring", 
                    damping: 15,
                    stiffness: 200
                  }}
                >
                  {msg.sender === "bot" && (
                    <div className="bot-avatar">
                      <img 
                        src="/logo.svg" 
                        alt="AJAN 007" 
                        className="agent-avatar"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'block';
                        }}
                      />
                      <svg
                        style={{ display: 'none' }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                        <line x1="9" y1="9" x2="9.01" y2="9"></line>
                        <line x1="15" y1="9" x2="15.01" y2="9"></line>
                      </svg>
                    </div>
                  )}
                  <motion.div 
                    className="message-content"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="message-text">{msg.text}</div>
                    <div className="message-time">{formatTime(msg.timestamp)}</div>
                  </motion.div>
                </motion.div>
              ))}
              
              {loading && (
                <motion.div 
                  className="message bot loading"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <div className="bot-avatar">
                    <img 
                      src="/logo.svg" 
                      alt="AJAN 007" 
                      className="agent-avatar"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'block';
                      }}
                    />
                    <svg
                      style={{ display: 'none' }}
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                      <line x1="9" y1="9" x2="9.01" y2="9"></line>
                      <line x1="15" y1="9" x2="15.01" y2="9"></line>
                    </svg>
                  </div>
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            <motion.div 
              className="chat-input"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('chatbot.inputPlaceholder')}
                rows="1"
              />
              <motion.button 
                onClick={sendMessage} 
                disabled={loading || !input.trim()}
                className={loading || !input.trim() ? "disabled" : ""}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 10 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </motion.button>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default Chatbot;